import React, { useState } from "react";

export interface LastMonthExpenseCardProps {
  monthLabel?: string;
  statusLabel?: string;
  statusVariant?: "healthy" | "watch" | "critical";
  expenseAmount?: string;
  momPercent?: string;
  revenuePercent?: string;
  /** Bar heights for mini chart (e.g. [40, 60, 55, 50, 80] for 5 months); last is current month */
  barHeights?: number[];
  barLabels?: string[];
  barAmounts?: string[];
  loading?: boolean;
}

function isBlank(value: unknown): boolean {
  return (
    value == null ||
    (typeof value === "string" && value.trim().length === 0)
  );
}

function truncateToDecimals(value: number, decimals: number): number {
  if (!Number.isFinite(value) || decimals < 0) return value;
  const factor = 10 ** decimals;
  return Math.trunc(value * factor) / factor;
}

function formatTruncatedPercentInText(
  input: string | undefined,
  decimals: number,
): string | undefined {
  if (isBlank(input)) return undefined;
  const text = String(input);
  const match = text.match(/([+-]?\d+(?:\.\d+)?)\s*%/);
  if (!match) return text;

  const parsed = Number(match[1]);
  if (!Number.isFinite(parsed)) return text;

  const truncated = truncateToDecimals(parsed, decimals);
  return text.replace(match[0], `${truncated.toFixed(decimals)}%`);
}

function LastMonthExpenseCardSkeleton() {
  return (
    <div
      className="flex flex-col items-start justify-between rounded-lg bg-[#ffeeee] px-6 py-6 flex-1 min-w-0 animate-pulse"
      aria-hidden
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-2">
          <div className="h-3.5 w-16 rounded bg-[#7d3e3e]/20" />
          <div className="h-5 w-32 rounded bg-[#7d3e3e]/20" />
        </div>
        <div className="h-7 w-16 rounded-full bg-[#7d3e3e]/20" />
      </div>
      <div className="flex w-full items-end justify-between gap-4">
        <div className="flex flex-col items-start gap-2">
          <div className="h-10 w-28 rounded bg-[#5c1f1f]/20" />
          <div className="h-4 w-14 rounded bg-[#7d3e3e]/20" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-end gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 w-6 rounded-sm bg-[#f5c2c7]/50" />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-3 w-5 rounded bg-[#7d3e3e]/20" />
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-7 w-20 rounded-full bg-[#7d3e3e]/20" />
        <div className="h-7 w-24 rounded-full bg-[#7d3e3e]/20" />
      </div>
    </div>
  );
}

export function LastMonthExpenseCard({
  monthLabel,
  statusLabel,
  statusVariant = "critical",
  expenseAmount,
  momPercent,
  revenuePercent,
  barHeights,
  barLabels,
  barAmounts,
  loading = false,
}: LastMonthExpenseCardProps) {
  const [hoveredBarIndex, setHoveredBarIndex] = useState<number | null>(null);

  if (loading) {
    return <LastMonthExpenseCardSkeleton />;
  }

  const safeBarHeights = Array.isArray(barHeights) ? barHeights : [];
  const safeBarLabels = Array.isArray(barLabels) ? barLabels : [];
  const canRenderBars =
    safeBarHeights.length > 0 &&
    safeBarLabels.length === safeBarHeights.length;

  const maxH = canRenderBars ? Math.max(...safeBarHeights, 1) : 1;

  const statusStyles =
    statusVariant === "healthy"
      ? {
          dot: "bg-success-500",
          text: "text-success-700",
          cardBg: "bg-[#e6f4ea]",
          barFrom: "from-[#bbf7d0]",
          barTo: "to-[#86efac]",
          line: "border-success-400",
          badgeBorder: "border-[#d1e9d6]",
          lastDotBorder: "border-success-600",
          titleText: "text-[#4a6b5a]",
          amountText: "text-[#1a3a2a]",
        }
      : statusVariant === "watch"
        ? {
            dot: "bg-warning-500",
            text: "text-warning-700",
            cardBg: "bg-[#fff7ed]",
            barFrom: "from-[#fed7aa]",
            barTo: "to-[#fdba74]",
            line: "border-warning-400",
            badgeBorder: "border-[#fed7aa]",
            lastDotBorder: "border-warning-600",
            titleText: "text-[#7c2d12]",
            amountText: "text-[#431407]",
          }
        : {
            dot: "bg-error-500",
            text: "text-error-700",
            cardBg: "bg-[#fef2f2]",
            barFrom: "from-[#fecaca]",
            barTo: "to-[#fca5a5]",
            line: "border-error-400",
            badgeBorder: "border-[#f5c2c7]",
            lastDotBorder: "border-error-600",
            titleText: "text-[#7d3e3e]",
            amountText: "text-[#5c1f1f]",
          };

  return (
    <div
      className={`flex flex-col items-start justify-between rounded-lg px-6 py-6 flex-1 min-w-0 ${statusStyles.cardBg}`}
    >
      <div className="flex w-full items-start justify-between">
        <div className="flex flex-col items-start gap-1">
          <span
            className={`text-caption-bold font-caption-bold uppercase tracking-wider ${statusStyles.titleText}`}
          >
            Last Month
          </span>
          <span className={`text-body font-body ${statusStyles.titleText}`}>
            {isBlank(monthLabel) ? "Last month unavailable" : monthLabel}
          </span>
        </div>
        {/* <div
          className={`flex items-center gap-2 rounded-full border border-solid bg-white px-3 py-1 ${statusStyles.badgeBorder}`}
        >
          <div
            className={`flex h-2 w-2 flex-none flex-col items-start rounded-full ${statusStyles.dot}`}
          />
          <span className={`text-body-bold font-body-bold ${statusStyles.text}`}>
            {statusLabel}
          </span>
        </div> */}
      </div>
      <div className="flex w-full items-end justify-between pb-4">
        <div className="flex flex-col items-start gap-1">
          <span
            className={`font-['Inter'] text-[40px] font-[700] leading-[40px] ${statusStyles.amountText}`}
          >
            {isBlank(expenseAmount) ? "—" : expenseAmount}
          </span>
          <span className={`text-body font-body ${statusStyles.titleText}`}>
            Expense
          </span>
        </div>
        <div className="flex flex-col items-center gap-2">
          {canRenderBars ? (
            <>
              <div className="flex items-end gap-2 relative">
                <div className="flex grow shrink-0 basis-0 items-center gap-1 absolute top-[40%] left-0">
                  <div
                    className={`flex items-start border-t-2 border-dashed flex-1 ${statusStyles.line}`}
                  />
                </div>
                {safeBarHeights.map((h, i) => {
                  const isLast = i === safeBarHeights.length - 1;
                  const label = safeBarLabels[i] ?? "";
                  const amount = barAmounts?.[i];
                  const isHovered = hoveredBarIndex === i;
                  return (
                    <div
                      key={i}
                      className="relative flex w-6 flex-none flex-col items-center rounded-sm cursor-default"
                      onMouseEnter={() => setHoveredBarIndex(i)}
                      onMouseLeave={() => setHoveredBarIndex(null)}
                    >
                      {isHovered && (amount != null || label) && (
                        <div
                          className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md border border-[#f5c2c7] bg-white px-2 py-1.5 shadow-md"
                          role="tooltip"
                        >
                          <span className="text-caption-bold font-caption-bold text-[#5c1f1f]">
                            {label}
                            {amount != null && ` · ${amount}`}
                          </span>
                        </div>
                      )}
                      <div
                        className={`flex w-full flex-col items-center rounded-sm transition-opacity ${
                          isLast
                            ? `pt-4 bg-gradient-to-b ${statusStyles.barFrom} ${statusStyles.barTo} relative`
                            : `bg-gradient-to-b ${statusStyles.barFrom} ${statusStyles.barTo}`
                        } ${isHovered ? "opacity-90" : ""}`}
                        style={{
                          height: Math.max(12, (h / maxH) * 80),
                        }}
                      >
                        {isLast && (
                          <div
                            className={`flex h-3 w-3 flex-none flex-col items-center rounded-full border-2 border-solid bg-white ${statusStyles.lastDotBorder}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                {safeBarLabels.map((l, i) => (
                  <span
                    key={i}
                    className={`text-caption font-caption ${statusStyles.titleText} ${i === safeBarLabels.length - 1 ? "text-caption-bold font-caption-bold" : ""}`}
                  >
                    {l}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="flex items-end gap-2" aria-hidden>
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 w-6 rounded-sm bg-white/40" />
                ))}
              </div>
              <span className={`text-caption font-caption ${statusStyles.titleText}`}>
                No trend data
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div
          className={`flex items-center rounded-full border border-solid bg-white px-3 py-1 ${statusStyles.badgeBorder}`}
        >
          <span
            className={`text-body-bold font-body-bold ${statusStyles.text}`}
          >
            {formatTruncatedPercentInText(momPercent, 2) ?? "MoM not available"}
          </span>
        </div>
        <div
          className={`flex items-center rounded-full border border-solid bg-white px-3 py-1 ${statusStyles.badgeBorder}`}
        >
          <span className="text-body-bold font-body-bold text-neutral-700">
            {formatTruncatedPercentInText(revenuePercent, 2) ??
              "Revenue % not available"}
          </span>
        </div>
      </div>
    </div>
  );
}
